import { useI18n } from '../../i18n'
import { readRequestHistory, readNoticeHistory, mergeHistoryRows } from './inbox-history-model.mjs'
import { readPendingRequests, pendingRequestKey } from './pending-request-model.mjs'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'
import {
  useWebSocket,
  type ConsoleInboxNoticesQuery,
  type ConsoleInboxNoticesResult,
} from '@zhin.js/client'
import type {
  EndpointInfo,
  InboxRequestRow,
  InboxNoticeRow,
  ReqItem,
  NoticeItem,
} from './types'
import { normalizeInboundContent } from '../../utils/parseComposerContent'
import { useInboxCacheScope } from '../../utils/useInboxCacheScope'
import { listInboxCache, putInboxCache, isInboxCacheScopeActive } from '../../utils/inbox-cache'
import { useChannelManager } from './useChannelManager'
import { useMessageHistory } from './useMessageHistory'
import { useGroupActions } from './useGroupActions'
import {
  subscribeConsoleRecoveryGap,
  subscribeEndpointPush,
  type EndpointPushMessage,
} from '../../utils/endpoint-push'
import { ENDPOINT_RPC, INBOX_RPC, SIDE_EVENT_PUSH, SIDE_EVENT_RPC } from '../../contracts/zhin-console'
import { readEndpointList } from '../endpoint-list-model.mjs'
import { readEndpointIdentity, endpointAddress, requestApproval, consumedRows } from './endpoint-request.mjs'
import { requestConsole } from '../../utils/console-rpc'

export function useEndpointConsole() {
  const i18n = useI18n()
  const { adapter: adapterParam, endpointId: endpointIdParam } = useParams<{
    adapter: string
    endpointId: string
  }>()
  const adapter = adapterParam ? decodeURIComponent(adapterParam) : ''
  const endpointId = endpointIdParam ? decodeURIComponent(endpointIdParam) : ''
  const endpointIdentity = `${adapter}\u0000${endpointId}`
  const endpointIdentityRef = useRef(endpointIdentity)
  endpointIdentityRef.current = endpointIdentity
  const valid = Boolean(adapter && endpointId)
  const cacheScope = useInboxCacheScope()
  const [searchParams] = useSearchParams()
  const requestedChannelType = searchParams.get('channelType')
  const initialChannelType = requestedChannelType === 'private'
    || requestedChannelType === 'group'
    || requestedChannelType === 'channel'
    ? requestedChannelType
    : undefined
  const initialChannelId = searchParams.get('channelId')?.trim() || undefined

  const { connected } = useWebSocket()
  const [info, setInfo] = useState<EndpointInfo | null>(null)
  const [loadErr, setLoadErr] = useState<string | null>(null)
  const [testLoading, setTestLoading] = useState(false)
  const [testMessage, setTestMessage] = useState<string | null>(null)
  const [actionError, setActionError] = useState<string | null>(null)

  // --- Endpoint info ---
  const infoSequence = useRef(0)
  const loadInfo = useCallback(async () => {
    if (!adapter || !endpointId) return
    const sequence = ++infoSequence.current
    try {
      const data = await requestConsole<EndpointInfo>({
        type: ENDPOINT_RPC.INFO,
        ...endpointAddress(adapter, endpointId),
      })
      if (sequence !== infoSequence.current || !isInboxCacheScopeActive(cacheScope)) return
      setInfo({ ...readEndpointList({ endpoints: [data] }, i18n.locale)[0], endpoint: readEndpointIdentity(data.endpoint, i18n.locale) })
      setLoadErr(null)
    } catch (e) {
      if (sequence === infoSequence.current && isInboxCacheScopeActive(cacheScope)) setLoadErr((e as Error).message)
    }
  }, [adapter, endpointId, cacheScope, i18n])

  useEffect(() => {
    setInfo(null)
    void loadInfo()
    const t = setInterval(loadInfo, 8000)
    return () => { clearInterval(t); infoSequence.current++ }
  }, [loadInfo])

  const testEndpoint = useCallback(async () => {
    if (!adapter || !endpointId) return
    setTestLoading(true)
    try {
      const result = await requestConsole<{ reachable: boolean; message: string; latencyMs: number }>({
        type: 'endpoint.test',
        adapter,
        endpointKey: endpointId,
      })
      setTestMessage(`${result.message} · ${result.latencyMs}ms`)
    } catch (error) {
      setTestMessage(i18n.t("测试失败：{0}", [(error as Error).message]))
    } finally {
      setTestLoading(false)
    }
  }, [adapter, endpointId, cacheScope, i18n])

  // --- Channel manager ---
  const channelMgr = useChannelManager({
    adapter,
    endpointId,
    info,
    initialChannelType,
    initialChannelId,
  })

  // --- Message history ---
  const msgHistory = useMessageHistory({
    adapter,
    endpointId,
    endpoint: info?.endpoint,
    selection: channelMgr.selection,
  })

  // --- Group actions ---
  const groupAct = useGroupActions({
    adapter,
    endpointId,
    selection: channelMgr.selection,
  })

  // --- Requests & notices state ---
  const [requests, setRequests] = useState<Map<string, ReqItem>>(new Map())
  const [notices, setNotices] = useState<Map<number, NoticeItem>>(new Map())
  const [inboxRequests, setInboxRequests] = useState<InboxRequestRow[]>([])
  const [inboxRequestsLoading, setInboxRequestsLoading] = useState(false)
  const [inboxRequestsHasMore, setInboxRequestsHasMore] = useState(false)
  const [inboxRequestsOffset, setInboxRequestsOffset] = useState(0)
  const [inboxRequestsEnabled, setInboxRequestsEnabled] = useState(false)
  const [inboxNotices, setInboxNotices] = useState<InboxNoticeRow[]>([])
  const [inboxNoticesLoading, setInboxNoticesLoading] = useState(false)
  const [inboxNoticesHasMore, setInboxNoticesHasMore] = useState(false)
  const [inboxNoticesOffset, setInboxNoticesOffset] = useState(0)
  const [inboxNoticesEnabled, setInboxNoticesEnabled] = useState(false)
  const [requestsTab, setRequestsTab] = useState<'pending' | 'history'>('pending')
  const [noticesTab, setNoticesTab] = useState<'unread' | 'history'>('unread')

  const [requestsLoading, setRequestsLoading] = useState(true)
  const [requestsError, setRequestsError] = useState<string | null>(null)
  const [requestsAvailable, setRequestsAvailable] = useState<boolean | null>(null)
  const [noticesLoading, setNoticesLoading] = useState(true)
  const [noticesError, setNoticesError] = useState<string | null>(null)
  const [noticesAvailable, setNoticesAvailable] = useState<boolean | null>(null)
  const [inboxRequestsError, setInboxRequestsError] = useState<string | null>(null)
  const [inboxNoticesError, setInboxNoticesError] = useState<string | null>(null)

  useEffect(() => {
    setRequests(new Map()); setNotices(new Map()); setInboxRequests([]); setInboxNotices([])
    setRequestsAvailable(null); setNoticesAvailable(null); setInboxRequestsEnabled(false); setInboxNoticesEnabled(false)
  }, [cacheScope])

  // --- Load requests from server ---
  const loadRequestsFromServer = useCallback(async () => {
    if (!adapter || !endpointId) return
    setRequestsLoading(true)
    setRequestsError(null)
    try {
      const response = await requestConsole<{ inboxEnabled: boolean; source: string }>({
        type: SIDE_EVENT_RPC.REQUEST_LIST,
        ...endpointAddress(adapter, endpointId),
      })
      if (!isInboxCacheScopeActive(cacheScope)) return
      const rows = readPendingRequests(response, i18n.locale)
      setRequestsAvailable(response.source === 'endpoint' || response.inboxEnabled === true)
      setRequests(new Map(rows.map(row => [pendingRequestKey(row), row])))
    } catch (caught) {
      if (isInboxCacheScopeActive(cacheScope)) setRequestsError(caught instanceof Error ? caught.message : String(caught))
    } finally {
      if (isInboxCacheScopeActive(cacheScope)) setRequestsLoading(false)
    }
  }, [adapter, endpointId, cacheScope, i18n])

  useEffect(() => {
    loadRequestsFromServer()
  }, [loadRequestsFromServer])

  // --- Load inbox requests ---
  // 递增序号：切换 endpoint 后在途的旧请求 resolve 时序号已过期，直接丢弃
  const inboxRequestsSeqRef = useRef(0)
  const inboxNoticesSeqRef = useRef(0)
  const unreadNoticesSeqRef = useRef(0)

  useEffect(() => {
    // Invalidate an in-flight unread projection immediately when the endpoint
    // identity changes, even before the new endpoint's cache hydration ends.
    unreadNoticesSeqRef.current += 1
  }, [adapter, endpointId, cacheScope])
  const loadInboxRequests = useCallback(
    async (append: boolean) => {
      if (!adapter || !endpointId) return
      const seq = ++inboxRequestsSeqRef.current
      setInboxRequestsLoading(true)
      setInboxRequestsError(null)
      try {
        const offset = append ? inboxRequestsOffset : 0
        const res = await requestConsole<{ requests: InboxRequestRow[]; inboxEnabled: boolean }>({
          type: INBOX_RPC.REQUESTS,
          ...endpointAddress(adapter, endpointId), limit: 30, offset,
        })
        if (seq !== inboxRequestsSeqRef.current || !isInboxCacheScopeActive(cacheScope)) return
        const rows = readRequestHistory(res, i18n.locale)
        setInboxRequestsEnabled(res.inboxEnabled)
        setInboxRequestsHasMore(res.inboxEnabled && rows.length === 30)
        setInboxRequestsOffset(offset + rows.length)
        if (!res.inboxEnabled || !rows.length) {
          if (!append) setInboxRequests([])
          return
        }
        if (append) {
          setInboxRequests((prev) => mergeHistoryRows(prev, rows))
        } else {
          setInboxRequests(rows)
        }
      } catch (caught) {
        // 一次性网络错误不改变 enabled 状态；仅服务端明确 inboxEnabled===false 才置 false
        if (seq !== inboxRequestsSeqRef.current || !isInboxCacheScopeActive(cacheScope)) return
        setInboxRequestsError(caught instanceof Error ? caught.message : String(caught))
      } finally {
        if (seq === inboxRequestsSeqRef.current && isInboxCacheScopeActive(cacheScope)) setInboxRequestsLoading(false)
      }
    },
    [adapter, endpointId, inboxRequestsOffset, cacheScope, i18n],
  )

  // --- Load inbox notices ---
  const loadInboxNotices = useCallback(
    async (append: boolean) => {
      if (!adapter || !endpointId) return
      const seq = ++inboxNoticesSeqRef.current
      setInboxNoticesLoading(true)
      setInboxNoticesError(null)
      try {
        const offset = append ? inboxNoticesOffset : 0
        const res = await requestConsole<{ notices: InboxNoticeRow[]; inboxEnabled: boolean }>({
          type: INBOX_RPC.NOTICES,
          ...endpointAddress(adapter, endpointId), limit: 30, offset,
        })
        if (seq !== inboxNoticesSeqRef.current || !isInboxCacheScopeActive(cacheScope)) return
        const rows = readNoticeHistory(res, i18n.locale)
        setInboxNoticesEnabled(res.inboxEnabled)
        setInboxNoticesHasMore(res.inboxEnabled && rows.length === 30)
        setInboxNoticesOffset(offset + rows.length)
        if (!res.inboxEnabled || !rows.length) {
          if (!append) setInboxNotices([])
          return
        }
        if (append) {
          setInboxNotices((prev) => mergeHistoryRows(prev, rows))
        } else {
          setInboxNotices(rows)
        }
      } catch (caught) {
        // 一次性网络错误不改变 enabled 状态；仅服务端明确 inboxEnabled===false 才置 false
        if (seq !== inboxNoticesSeqRef.current || !isInboxCacheScopeActive(cacheScope)) return
        setInboxNoticesError(caught instanceof Error ? caught.message : String(caught))
      } finally {
        if (seq === inboxNoticesSeqRef.current && isInboxCacheScopeActive(cacheScope)) setInboxNoticesLoading(false)
      }
    },
    [adapter, endpointId, inboxNoticesOffset, cacheScope, i18n],
  )

  const loadNoticesFromServer = useCallback(async () => {
    if (!adapter || !endpointId) return
    const seq = ++unreadNoticesSeqRef.current
    const requestedEndpoint = `${adapter}\u0000${endpointId}`
    setNoticesLoading(true)
    setNoticesError(null)
    try {
      const res = await requestConsole<ConsoleInboxNoticesResult>({
        type: INBOX_RPC.NOTICES,
        ...{
          adapter,
          endpointKey: endpointId,
          unreadOnly: true,
          limit: 100,
          offset: 0,
        } satisfies ConsoleInboxNoticesQuery,
      })
      if (
        seq !== unreadNoticesSeqRef.current
        || requestedEndpoint !== endpointIdentityRef.current
        || !isInboxCacheScopeActive(cacheScope)
      ) return
      setNoticesAvailable(res.inboxEnabled)
      if (!res.inboxEnabled) return
      setNotices(new Map((res.notices ?? []).map((notice) => [notice.id, {
        id: notice.id,
        noticeType: notice.noticeType,
        channel: notice.channel,
        payload: notice.payload,
        timestamp: notice.timestamp,
      }])))
    } catch (caught) {
      if (seq === unreadNoticesSeqRef.current && requestedEndpoint === endpointIdentityRef.current && isInboxCacheScopeActive(cacheScope)) {
        setNoticesError(caught instanceof Error ? caught.message : String(caught))
      }
    } finally {
      if (seq === unreadNoticesSeqRef.current && isInboxCacheScopeActive(cacheScope)) setNoticesLoading(false)
    }
  }, [adapter, endpointId, cacheScope])

  // A recovery gap means every projection fed by the bounded event journal may
  // be incomplete. Rebuild the request and notice views from their
  // authoritative HTTP APIs; message history performs the same recovery in
  // useMessageHistory for the currently selected conversation.
  useEffect(() => {
    if (!adapter || !endpointId) return
    return subscribeConsoleRecoveryGap(() => {
      setRequests(new Map())
      setNotices(new Map())
      setInboxRequests([])
      setInboxRequestsOffset(0)
      setInboxNotices([])
      setInboxNoticesOffset(0)
      void Promise.all([
        loadRequestsFromServer(),
        loadNoticesFromServer(),
        loadInboxRequests(false),
        loadInboxNotices(false),
      ])
    })
  }, [
    adapter,
    endpointId,
    loadInboxNotices,
    loadInboxRequests,
    loadNoticesFromServer,
    loadRequestsFromServer,
  ])

  // --- Hydrate requests/notices/messages from local inbox cache ---
  useEffect(() => {
    if (!adapter || !endpointId) return
    let cancelled = false
    void (async () => {
      const [cachedRequests, cachedNotices] = await Promise.all([
        listInboxCache(adapter, endpointId, 'request'),
        listInboxCache(adapter, endpointId, 'notice'),
      ])
      if (cancelled || !isInboxCacheScopeActive(cacheScope)) return

      if (cachedRequests.length) {
        setRequests((prev) => {
          const m = new Map(prev)
          for (const rec of cachedRequests) {
            const d = rec.payload
            const id = d.id as number
            if (id == null) continue
            m.set(pendingRequestKey({id,platformRequestId:String(d.platformRequestId ?? '')}), {
              id,
              platformRequestId: String(d.platformRequestId ?? ''),
              type: String(d.type ?? ''),
              sender: (d.sender as ReqItem['sender']) ?? { id: '', name: '' },
              comment: String(d.comment ?? ''),
              channel: (d.channel as ReqItem['channel']) ?? { id: '', type: 'private' },
              timestamp: Number(d.timestamp ?? rec.updatedAt),
              canAct: d.canAct === true,
            })
          }
          return m
        })
      }

      if (cachedNotices.length) {
        setNotices((prev) => {
          const m = new Map(prev)
          for (const rec of cachedNotices) {
            const d = rec.payload
            const id = d.id as number
            if (id == null) continue
            m.set(id, {
              id,
              noticeType: String(d.noticeType ?? ''),
              channel: (d.channel as NoticeItem['channel']) ?? { id: '', type: 'private' },
              payload: String(d.payload ?? '{}'),
              timestamp: Number(d.timestamp ?? rec.updatedAt),
            })
          }
          return m
        })
      }

      if (cachedRequests.length) {
        setInboxRequests((prev) => {
          if (prev.length) return prev
          return cachedRequests.map((rec, idx) => {
            const d = rec.payload
            return {
              id: Number(d.id ?? idx),
              platform_request_id: String(d.platform_request_id ?? d.platformRequestId ?? ''),
              type: String(d.type ?? ''),
              sub_type: d.sub_type != null ? String(d.sub_type) : null,
              channel_id: String(d.channel_id ?? d.channelId ?? ''),
              channel_type: String(d.channel_type ?? d.channelType ?? 'private'),
              sender_id: String(d.sender_id ?? ''),
              sender_name: d.sender_name != null ? String(d.sender_name) : null,
              comment: d.comment != null ? String(d.comment) : null,
              created_at: Number(d.created_at ?? rec.updatedAt),
              resolved: Number(d.resolved ?? 0),
              resolved_at: d.resolved_at != null ? Number(d.resolved_at) : null,
            } satisfies InboxRequestRow
          })
        })
        setInboxRequestsEnabled(true)
      }

      if (cachedNotices.length) {
        setInboxNotices((prev) => {
          if (prev.length) return prev
          return cachedNotices.map((rec, idx) => {
            const d = rec.payload
            return {
              id: Number(d.id ?? idx),
              platform_notice_id: String(d.platform_notice_id ?? ''),
              type: String(d.type ?? d.noticeType ?? ''),
              sub_type: d.sub_type != null ? String(d.sub_type) : null,
              channel_id: String(d.channel_id ?? ''),
              channel_type: String(d.channel_type ?? 'private'),
              operator_id: d.operator_id != null ? String(d.operator_id) : null,
              operator_name: d.operator_name != null ? String(d.operator_name) : null,
              target_id: d.target_id != null ? String(d.target_id) : null,
              target_name: d.target_name != null ? String(d.target_name) : null,
              payload: String(d.payload ?? '{}'),
              created_at: Number(d.created_at ?? rec.updatedAt),
            } satisfies InboxNoticeRow
          })
        })
        setInboxNoticesEnabled(true)
      }

      // Cache is only a fast paint. The durable unread projection is the
      // authority after refresh and removes notices already consumed elsewhere.
      await loadNoticesFromServer()
    })()
    return () => {
      cancelled = true
    }
  }, [adapter, endpointId, loadNoticesFromServer, cacheScope])

  // --- Listen for real-time push events for requests and notices ---
  useEffect(() => {
    const onPush = (msg: EndpointPushMessage) => {
      if (!isInboxCacheScopeActive(cacheScope)) return
      if (msg.type === SIDE_EVENT_PUSH.REQUEST_RECEIVE) {
        const request = msg.data
        if (request.adapter === adapter && request.endpointKey === endpointId) {
          void putInboxCache(adapter, endpointId, 'request', { ...request })
          setRequests((prev) => {
            const m = new Map(prev)
            m.set(pendingRequestKey(request), {
              id: request.id,
              platformRequestId: request.platformRequestId,
              type: request.type,
              sender: request.sender,
              comment: request.comment,
              channel: request.channel,
              timestamp: request.timestamp,
              canAct: request.canAct === true,
            })
            return m
          })
        }
      } else if (msg.type === SIDE_EVENT_PUSH.NOTICE_RECEIVE) {
        const notice = msg.data
        if (notice.adapter === adapter && notice.endpointKey === endpointId) {
          void putInboxCache(adapter, endpointId, 'notice', { ...notice })
          setNotices((prev) => {
            const m = new Map(prev)
            m.set(notice.id, {
              id: notice.id,
              noticeType: notice.noticeType,
              channel: notice.channel,
              payload: notice.payload,
              timestamp: notice.timestamp,
            })
            return m
          })
        }
      }
    }
    return subscribeEndpointPush(onPush)
  }, [adapter, endpointId, cacheScope])

  // --- Derived lists ---
  const requestList = useMemo(() => [...requests.values()].sort((a, b) => b.timestamp - a.timestamp), [requests])
  const noticeList = useMemo(() => [...notices.values()].sort((a, b) => b.timestamp - a.timestamp), [notices])

  // --- Request/notice actions ---
  const approve = useCallback(
    async (platformRequestId: string, approveIt: boolean) => {
      setActionError(null)
      try {
        await requestConsole({
          type: approveIt ? SIDE_EVENT_RPC.REQUEST_APPROVE : SIDE_EVENT_RPC.REQUEST_REJECT,
          ...requestApproval(adapter, endpointId, platformRequestId),
        })
        const row = requestList.find((r) => r.platformRequestId === platformRequestId)
        if (row) {
          setRequests((prev) => {
            const m = new Map(prev)
            m.delete(pendingRequestKey(row))
            return m
          })
        }
      } catch (e) {
        setActionError((e as Error).message)
      }
    },
    [adapter, endpointId, requestList],
  )

  const dismissRequest = useCallback(
    async (id: number) => {
      setActionError(null)
      try {
        await requestConsole({ type: SIDE_EVENT_RPC.REQUEST_CONSUMED, ...consumedRows(id) })
        setRequests((prev) => {
          const m = new Map(prev)
          for (const [key, request] of m) if (request.id === id) m.delete(key)
          return m
        })
      } catch (e) {
        setActionError((e as Error).message)
      }
    },
    [],
  )

  const dismissNotice = useCallback(
    async (id: number) => {
      setActionError(null)
      try {
        await requestConsole({ type: SIDE_EVENT_RPC.NOTICE_CONSUMED, ...consumedRows(id) })
        setNotices((prev) => {
          const m = new Map(prev)
          m.delete(id)
          return m
        })
      } catch (e) {
        setActionError((e as Error).message)
      }
    },
    [],
  )

  const refreshNotices = useCallback(async () => {
    if (!adapter || !endpointId) return
    await loadNoticesFromServer()
    if (noticesTab === 'history') {
      await loadInboxNotices(false)
    }
  }, [adapter, endpointId, noticesTab, loadInboxNotices, loadNoticesFromServer])

  const showRightPanel =
    channelMgr.selection?.type === 'channel' && channelMgr.selection.channelType === 'group' && adapter === 'icqq'

  // --- Public API (preserves original shape) ---
  return {
    valid,
    adapter,
    endpointId,
    connected,
    info,
    loadErr,
    testLoading,
    testMessage,
    testEndpoint,
    actionError,
    // message sending
    msgContent: msgHistory.msgContent,
    setMsgContent: msgHistory.setMsgContent,
    sending: msgHistory.sending,
    // channel manager
    listLoading: channelMgr.listLoading,
    listNotice: channelMgr.listNotice,
    selection: channelMgr.selection,
    setSelection: channelMgr.setSelection,
    showChannelList: channelMgr.showChannelList,
    setShowChannelList: channelMgr.setShowChannelList,
    listSearch: channelMgr.listSearch,
    setListSearch: channelMgr.setListSearch,
    channels: channelMgr.channels,
    filteredChannels: channelMgr.filteredChannels,
    conversationSections: channelMgr.conversationSections,
    sectionCollapsed: channelMgr.sectionCollapsed,
    systemSectionCollapsed: channelMgr.systemSectionCollapsed,
    toggleSection: channelMgr.toggleSection,
    toggleSystemSection: channelMgr.toggleSystemSection,
    markConversationRead: channelMgr.markConversationRead,
    deleteFriend: channelMgr.deleteFriend,
    getChannelIcon: channelMgr.getChannelIcon,
    loadLists: channelMgr.loadLists,
    // message history
    channelMessages: msgHistory.channelMessages,
    inboxMessagesLoading: msgHistory.inboxMessagesLoading,
    inboxMessagesError: msgHistory.inboxMessagesError,
    inboxMessagesHasMore: msgHistory.inboxMessagesHasMore,
    inboxMessagesEnabled: msgHistory.inboxMessagesEnabled,
    loadInboxMessages: msgHistory.loadInboxMessages,
    inboxMessages: msgHistory.inboxMessages,
    handleSend: msgHistory.handleSend,
    // group actions
    members: groupAct.members,
    membersLoading: groupAct.membersLoading,
    loadMembers: groupAct.loadMembers,
    groupAction: groupAct.groupAction,
    // requests & notices
    requestList,
    requestsLoading,
    requestsError,
    requestsAvailable,
    noticeList,
    noticesLoading,
    noticesError,
    noticesAvailable,
    inboxRequestsError,
    inboxNoticesError,
    requestsTab,
    setRequestsTab,
    noticesTab,
    setNoticesTab,
    inboxRequests,
    inboxRequestsLoading,
    inboxRequestsHasMore,
    inboxRequestsEnabled,
    loadInboxRequests,
    inboxNotices,
    inboxNoticesLoading,
    inboxNoticesHasMore,
    inboxNoticesEnabled,
    loadInboxNotices,
    loadRequestsFromServer,
    refreshNotices,
    approve,
    dismissRequest,
    dismissNotice,
    showRightPanel,
  }
}
